import _get from 'lodash/get'
import React, { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { View } from 'react-native'

import type { StepContentRendererProps } from '@flows-ui/types'
import { useStepContentText } from '@flows-ui/utils/useStepContentText'
import { BUTTON_IDS } from '@flows/configs/analytics/constants'
import type {
  AnyStepContentElement,
  ItemActionList as ItemActionListProps,
} from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import ItemActionRow from '@ui-kit/components/listComponents/ItemActionRow'

interface Props extends ItemActionListProps {
  flowValues: FlowValues
  submitHandler: NonNullable<StepContentRendererProps<AnyStepContentElement>['onSubmit']>
  isSubmitting?: boolean
}

const CLEAR_OPTIONS = { shouldDirty: false, shouldValidate: false }

const isItemRecord = (item: unknown): item is Record<string, unknown> =>
  typeof item === 'object' && item !== null && !Array.isArray(item)

const toItemLabel = (
  value: unknown,
  translate: ReturnType<typeof useStepContentText>,
  translationKeyPrefix?: string
) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return ''
  }
  return translationKeyPrefix
    ? translate({ type: 'translatableText', translationKey: `${translationKeyPrefix}${value}` })
    : String(value)
}

const useItemAction = (props: Props) => {
  const { setValue, handleSubmit } = useFormContext()
  const pendingRef = useRef(false)
  const [isPending, setIsPending] = useState(false)
  const setPending = (pending: boolean) => {
    pendingRef.current = pending
    setIsPending(pending)
  }

  useEffect(() => {
    setValue(props.actionTransientPath, undefined, CLEAR_OPTIONS)
  }, [setValue, props.actionTransientPath])

  const submitAction = async (action: number | 'new') => {
    if (pendingRef.current || props.isSubmitting) {
      return
    }
    setPending(true)
    try {
      setValue(props.actionTransientPath, action)
      await handleSubmit(props.submitHandler)()
    } catch (error) {
      console.error('itemActionList: submitting the step failed', error)
    } finally {
      setValue(props.actionTransientPath, undefined, CLEAR_OPTIONS)
      setPending(false)
    }
  }

  return { isDisabled: Boolean(props.isSubmitting) || isPending, submitAction }
}

const toItemEntries = (flowValues: FlowValues, name: string) => {
  const storedItems: unknown = _get(flowValues, name)
  return Array.isArray(storedItems)
    ? storedItems.flatMap((item: unknown, index) => (isItemRecord(item) ? [{ item, index }] : []))
    : []
}

const canAddItem = (itemCount: number, maximumItems?: number) =>
  maximumItems === undefined || itemCount < maximumItems

export const ItemActionList = (props: Props) => {
  const translateFunction = useStepContentText(props.flowValues)
  const { isDisabled, submitAction } = useItemAction(props)
  const entries = toItemEntries(props.flowValues, props.name)
  const editLabel = translateFunction(props.editItemLabel)

  return (
    <View className="gap-4">
      {entries.map(({ item, index }) => (
        <ItemActionRow
          key={index}
          id={`${BUTTON_IDS.listEditButton}-${index}`}
          iconName={props.itemIconName ?? 'CheckCircle'}
          title={toItemLabel(
            item[props.itemTitleKey],
            translateFunction,
            props.itemTitleTranslationKeyPrefix
          )}
          subtitle={
            props.itemSubtitleKey
              ? toItemLabel(
                  item[props.itemSubtitleKey],
                  translateFunction,
                  props.itemSubtitleTranslationKeyPrefix
                )
              : undefined
          }
          actionIconName="PencilSimple"
          actionLabel={editLabel}
          disabled={isDisabled}
          onPress={() => submitAction(index)}
        />
      ))}
      {canAddItem(entries.length, props.maximumItems) ? (
        <ItemActionRow
          id={BUTTON_IDS.listAddButton}
          iconName="PlusCircle"
          iconWeight="fill"
          title={translateFunction(props.addItemLabel)}
          actionIconName="PlusCircle"
          actionLabel={props.addActionLabel ? translateFunction(props.addActionLabel) : undefined}
          disabled={isDisabled}
          onPress={() => submitAction('new')}
        />
      ) : null}
    </View>
  )
}
